import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { FileInput } from '@/components/ui/file-input';
import { Volume2, VolumeX, Upload, Trash2, Play, Check } from 'lucide-react';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { notificationSoundService, NotificationSound } from '@/services/notificationSoundService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const NotificationSoundSettings = () => {
  const { t } = useTranslation();
  const { soundEnabled, toggleSound, playSound, setVolume, refreshCustomSound } = useNotificationSound();
  const [volumeValue, setVolumeValue] = useState([50]);
  const [customSounds, setCustomSounds] = useState<NotificationSound[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [soundToDelete, setSoundToDelete] = useState<string | null>(null);
  const [previewingSound, setPreviewingSound] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        loadUserSounds(user.id);
      }
    };
    getUser();
  }, []);

  const loadUserSounds = async (uid: string) => {
    setIsLoading(true);
    try {
      const sounds = await notificationSoundService.getUserSounds(uid);
      setCustomSounds(sounds);
    } catch (error) {
      console.error('Error loading sounds:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    setVolumeValue(value);
    setVolume(value[0] / 100);
  };

  const handleTestSound = () => {
    playSound();
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !userId) return;

    const file = files[0];
    const soundName = file.name.replace(/\.[^/.]+$/, '');

    setIsUploading(true);
    try {
      const canAdd = await notificationSoundService.canAddMoreSounds(userId);
      if (!canAdd) {
        toast.error(t('settings.notifications.maxSoundsReached'));
        return;
      }

      const validation = await notificationSoundService.validateAudioFile(file);
      if (!validation.valid) {
        const errorKey = `settings.notifications.${validation.error}`;
        toast.error(t(errorKey));
        return;
      }

      await notificationSoundService.uploadSound(userId, file, soundName);
      toast.success(t('settings.notifications.uploadSuccess'));
      await loadUserSounds(userId);
    } catch (error: any) {
      console.error('Error uploading sound:', error);
      const errorKey = `settings.notifications.${error.message}`;
      toast.error(t(errorKey) || t('settings.notifications.uploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSetActive = async (soundId: string) => {
    if (!userId) return;

    try {
      await notificationSoundService.setActiveSound(userId, soundId);
      await loadUserSounds(userId);
      await refreshCustomSound();
      toast.success(t('settings.notifications.soundActivated'));
    } catch (error) {
      console.error('Error setting active sound:', error);
      toast.error(t('settings.notifications.errorActivating'));
    }
  };

  const handleUseDefault = async () => {
    if (!userId) return;

    try {
      await notificationSoundService.useDefaultSound(userId);
      await loadUserSounds(userId);
      await refreshCustomSound();
      toast.success(t('settings.notifications.defaultActivated'));
    } catch (error) {
      console.error('Error using default sound:', error);
      toast.error(t('settings.notifications.errorActivating'));
    }
  };

  const handleDeleteClick = (soundId: string) => {
    setSoundToDelete(soundId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!soundToDelete || !userId) return;

    try {
      await notificationSoundService.deleteSound(userId, soundToDelete);
      toast.success(t('settings.notifications.deleteSuccess'));
      await loadUserSounds(userId);
      await refreshCustomSound();
    } catch (error) {
      console.error('Error deleting sound:', error);
      toast.error(t('settings.notifications.deleteError'));
    } finally {
      setDeleteDialogOpen(false);
      setSoundToDelete(null);
    }
  };

  const handlePreviewSound = async (sound: NotificationSound) => {
    if (previewingSound === sound.id) {
      setPreviewingSound(null);
      return;
    }

    setPreviewingSound(sound.id);
    try {
      const url = await notificationSoundService.getSoundUrl(sound.file_path);
      const audio = new Audio(url);
      audio.volume = volumeValue[0] / 100;
      audio.play();
      audio.onended = () => setPreviewingSound(null);
    } catch (error) {
      console.error('Error previewing sound:', error);
      setPreviewingSound(null);
    }
  };

  const isDefaultActive = customSounds.length === 0 || !customSounds.some(s => s.is_active);

  return (
    <>
      <Card className="p-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-1">
              {t('settings.notifications.soundTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('settings.notifications.soundDescription')}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notification-sound" className="text-base">
                {t('settings.notifications.enableSound')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('settings.notifications.enableSoundDescription')}
              </p>
            </div>
            <Switch
              id="notification-sound"
              checked={soundEnabled}
              onCheckedChange={toggleSound}
            />
          </div>

          {soundEnabled && (
            <>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  {volumeValue[0] === 0 ? (
                    <VolumeX className="h-4 w-4" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                  {t('settings.notifications.volume')} ({volumeValue[0]}%)
                </Label>
                <Slider
                  value={volumeValue}
                  onValueChange={handleVolumeChange}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">{t('settings.notifications.customSounds')}</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('settings.notifications.customSoundsDescription')}
                  </p>
                </div>

                {/* Default sound option */}
                <div 
                  className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={handleUseDefault}
                >
                  <div className="flex items-center gap-3">
                    <Volume2 className="h-5 w-5" />
                    <div>
                      <p className="font-medium">{t('settings.notifications.defaultSound')}</p>
                      <p className="text-xs text-muted-foreground">{t('settings.notifications.systemDefault')}</p>
                    </div>
                  </div>
                  {isDefaultActive && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </div>

                {/* Custom sounds list */}
                {isLoading ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('common.loading')}...
                  </p>
                ) : (
                  customSounds.map((sound) => (
                    <div 
                      key={sound.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewSound(sound)}
                        >
                          <Play className={`h-4 w-4 ${previewingSound === sound.id ? 'text-primary' : ''}`} />
                        </Button>
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => handleSetActive(sound.id)}
                        >
                          <p className="font-medium">{sound.sound_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {Math.round(sound.file_size / 1024)}KB
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {sound.is_active && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(sound.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}

                {/* Upload button */}
                {customSounds.length < 5 && (
                  <div>
                    <Label htmlFor="sound-upload" className="cursor-pointer">
                      <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg hover:bg-accent/50 transition-colors">
                        <Upload className="h-5 w-5" />
                        <span>{isUploading ? t('common.uploading') + '...' : t('settings.notifications.uploadSound')}</span>
                      </div>
                    </Label>
                    <FileInput
                      id="sound-upload"
                      accept="audio/mp3,audio/mpeg,audio/wav,audio/ogg"
                      onFileChange={handleFileUpload}
                      disabled={isUploading}
                      className="hidden"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('settings.notifications.uploadLimits')}
                    </p>
                  </div>
                )}

                {customSounds.length >= 5 && (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    {t('settings.notifications.maxSoundsReached')}
                  </p>
                )}
              </div>

              <Separator />

              <Button
                variant="outline"
                onClick={handleTestSound}
                className="w-full"
              >
                <Volume2 className="h-4 w-4 mr-2" />
                {t('settings.notifications.testSound')}
              </Button>
            </>
          )}
        </div>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.notifications.deleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.notifications.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
